/* This Component provides a new version of the SponsoredTextOffers component which is rethought
and updated for the desktop views. The analog in the canJs code base is dynamicFeaturedTextOffersBase_control
This component is used for now on the sweeps page only and implements functionality that is now needed there.
For future needs, it should be expanded with functionality that is needed on other pages. */

import React from 'react';
import { useSelector } from 'react-redux';

import isEmpty from 'lodash/isEmpty';

import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import List from 'core/modules/Partials/List/List';
import OfferTextItem from 'core/modules/OfferTextItem/OfferTextItem';
import HorizontalOffer from 'core/modules/SponsoredOffers/components/HorizontalOffer/HorizontalOffer';
import FeaturedDealDesktop
  from 'core/modules/FeaturedDeals/components/FeaturedDeal/FeaturedDealDesktop/FeaturedDealDesktop';
import FeaturedDealMobileRounded
  from 'core/modules/FeaturedDeals/components/FeaturedDeal/FeaturedDealMobileRounded/FeaturedDealMobileRounded';

import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';

import { useGetSponsoredOffers } from './hooks/useGetSponsoredOffers';

import './SponsoredOffers.scss';

function SponsoredOffers({
  namespace = 'default', customParams = {}, content_group_id, section_title: title,
  pill, view_type: viewType, customClassName = '',
}) {
  const customApiParams = { ...customParams, content_group_id };

  const isMobileMQ = useSelector(selectIsMobileMQ);
  const { data, config } = useGetSponsoredOffers(namespace, customApiParams);

  const VIEW_TYPE_LIST_1_COLUMN = '1_column_grid';
  const VIEW_TYPE_LIST_2_COLUMNS = '2_column_grid';
  const VIEW_TYPE_GRID_3_COLUMNS = '3_column_grid';
  const VIEW_TYPE_GRID_4_COLUMNS = '4_column_grid';
  const VIEW_TYPE_CAROUSEL_3_COLUMNS = '3_column_carousel';
  const VIEW_TYPE_CAROUSEL_4_COLUMNS = '4_column_carousel';
  const HORIZONTAL_VIEW_TYPES = [VIEW_TYPE_LIST_1_COLUMN, VIEW_TYPE_LIST_2_COLUMNS];
  const GRID_COLUMNS_VIEW_TYPES = [VIEW_TYPE_GRID_4_COLUMNS, VIEW_TYPE_GRID_3_COLUMNS];
  const CAROUSEL_VIEW_TYPES = [VIEW_TYPE_CAROUSEL_3_COLUMNS, VIEW_TYPE_CAROUSEL_4_COLUMNS];

  const shouldUseFeaturedDealsGridView = !isMobileMQ && GRID_COLUMNS_VIEW_TYPES.includes(viewType);
  const shouldUseHorizontalView = !isMobileMQ && HORIZONTAL_VIEW_TYPES.includes(viewType);
  const shouldUseFeaturedDealsCarouselView = !isMobileMQ && CAROUSEL_VIEW_TYPES.includes(viewType);

  const shouldUseCarousel = shouldUseFeaturedDealsCarouselView && !shouldUseFeaturedDealsGridView;

  const viewTypeSectionClassName = `mn_${viewType}`;

  const { api: { options }, shouldUseMobileRoundedView, carousel } = config;
  const { sectionId } = options;

  const renderItems = () => {
    if (isMobileMQ && shouldUseMobileRoundedView) {
      return (
        <div className="mn_featuredDeals">
          {data.map(dataItem => (
            <FeaturedDealMobileRounded
              key={dataItem.id}
              offer={dataItem}
              config={config}
            />
          ))}
        </div>
      );
    }

    if (shouldUseHorizontalView) {
      const viewTypeClassName = viewType === VIEW_TYPE_LIST_1_COLUMN ? 'mn_singleColumn' : 'mn_twoColumns';

      return (
        <div className={`mn_horizontalOffers ${viewTypeClassName}`}>
          {data.map(dataItem => <HorizontalOffer key={dataItem.id} offer={dataItem} config={config} />)}
        </div>
      );
    }

    if (shouldUseFeaturedDealsGridView || shouldUseFeaturedDealsCarouselView) {
      return (
        <div className="mn_featuredDeals">
          <List options={carousel} shouldUseCarousel={shouldUseCarousel} hasFillers={isMobileMQ ? 1 : 4}>
            {data.map(dataItem => (
              <FeaturedDealDesktop
                key={dataItem.id}
                offer={dataItem}
                config={config}
              />
            ))}
          </List>
        </div>
      );
    }

    return (
      <div className="mn_sponsoredOffersItems">
        {data.map(dataItem => (
          <OfferTextItem
            key={dataItem.id}
            dataItem={dataItem}
            sectionId={sectionId}
            className="mn_sponsoredTextOffer"
          />
        ))}
      </div>
    );
  };

  if (isEmpty(data)) {
    return null;
  }

  return (
    <div className={`mn_sponsoredOffers ${customClassName} ${viewTypeSectionClassName}`}>
      <SectionTitle title={title} pill={pill} />
      <div className="mn_sponsoredOffersItems">
        {renderItems()}
      </div>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><SponsoredOffers {...props} /></ComponentErrorBoundary>;
